/* ClientReportDocument — branded, print/PDF-friendly per-client activity report.
 * Rendered off-screen and captured by exportNodeToPdf (same engine as proposals). */
import { forwardRef } from 'react';

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }); } catch { return iso || ''; } };

export const ClientReportDocument = forwardRef(({ company, client, window: win, summary, byPlatform, recentPosts }, ref) => {
  const brand = client?.brand_color || '#4f46e5';
  const kpis = [
    { label: 'Published', value: summary?.published ?? 0 },
    { label: 'Scheduled', value: summary?.scheduled ?? 0 },
    { label: 'Failed', value: summary?.failed ?? 0 },
    { label: 'Success rate', value: summary?.success_rate != null ? `${summary.success_rate}%` : '—' },
  ];
  return (
    <div ref={ref} className="bg-white p-8" style={{ maxWidth: 720, margin: '0 auto' }} data-testid="client-report-document">
      <div className="flex items-center justify-between gap-3 pb-4" style={{ borderBottom: `3px solid ${brand}` }}>
        <div className="flex items-center gap-3">
          <div style={{ height: 40, width: 40, borderRadius: 8, background: brand }} />
          <div>
            <p className="font-bold text-lg text-zinc-900">{client?.name || 'Client'}</p>
            <p className="text-xs text-zinc-500">Activity report</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium text-zinc-700">{company || 'PostToSocial'}</p>
          <p className="text-xs text-zinc-400">{fmtDate(win?.start)} — {fmtDate(win?.end)}</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 mt-6">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-lg border border-zinc-200 p-3 text-center">
            <p className="text-2xl font-bold text-zinc-900">{k.value}</p>
            <p className="text-[11px] text-zinc-500 mt-0.5">{k.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-7">
        <h3 className="text-sm font-semibold text-zinc-900 mb-2">By platform</h3>
        {(!byPlatform || byPlatform.length === 0) ? (
          <p className="text-sm text-zinc-400">No platform activity in this range.</p>
        ) : (
          <div className="border border-zinc-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-4 bg-zinc-50 text-[11px] font-medium text-zinc-500 px-3 py-2">
              <span>Platform</span><span className="text-center">Published</span><span className="text-center">Total</span><span className="text-right">Success</span>
            </div>
            {byPlatform.map((p) => (
              <div key={p.platform} className="grid grid-cols-4 px-3 py-2 border-t border-zinc-100 text-sm">
                <span className="capitalize text-zinc-800">{p.platform}</span>
                <span className="text-center">{p.published}</span>
                <span className="text-center text-zinc-500">{p.total}</span>
                <span className="text-right">{p.success_rate != null ? `${p.success_rate}%` : '—'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-7">
        <h3 className="text-sm font-semibold text-zinc-900 mb-2">Recent published posts</h3>
        {(!recentPosts || recentPosts.length === 0) ? (
          <p className="text-sm text-zinc-400">No published posts in this range.</p>
        ) : (
          <ul className="space-y-2">
            {recentPosts.map((post) => (
              <li key={post.id} className="flex items-start gap-3 border-b border-zinc-100 pb-2">
                <span className="text-[11px] text-zinc-400 whitespace-nowrap mt-0.5 w-20 shrink-0">{fmtDate(post.date)}</span>
                <span className="text-[11px] font-medium capitalize text-white px-1.5 py-0.5 rounded shrink-0" style={{ background: brand }}>{post.platform || 'post'}</span>
                <span className="text-sm text-zinc-700 line-clamp-2">{post.content || '(no text)'}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-center text-[10px] text-zinc-400 mt-8">Prepared by {company || 'PostToSocial'} · Powered by PostToSocial</p>
    </div>
  );
});

ClientReportDocument.displayName = 'ClientReportDocument';
export default ClientReportDocument;
